import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { Building2, FileText, LayoutDashboard, UsersRound, Upload } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { SectionLoader } from '@/components/common/SectionLoader';
import { PermissionGate } from '@/components/common/PermissionGate';
import { usePermissions } from '@/hooks/usePermissions';
import { ConfirmDialog } from '@/components/ui';
import { useSetupWizardStatus } from '@/features/setup-wizard/hooks/useSetupWizard';
import type { CompanySetupStatus, SetupWizardStatus } from '@/types/setup-wizard.types';
import { useCompany, useDeleteCompany } from '../hooks/useCompanies';
import { CompanyOverviewTab } from './CompanyOverviewTab';
import { CompanySettingsTab } from './CompanySettingsTab';
import { SetupChecklistStrip } from './company-detail/SetupChecklistStrip';

/** The org-wide status shape lacks `company`; only the per-company one is usable here. */
function isCompanySetupStatus(
  status: CompanySetupStatus | SetupWizardStatus | undefined
): status is CompanySetupStatus {
  return Boolean(status && 'company' in status);
}

const TAB_VALUES = ['overview', 'settings'] as const;

/** HR screens moved to People & HR; old company links land on the same screen there. */
const HR_REDIRECTS: Record<string, string> = {
  structure: 'tab=setup',
  employment: 'tab=people',
  'org-chart': 'tab=people&view=chart',
  movements: 'tab=changes',
  probation: 'tab=changes&view=probation',
};
type TabValue = (typeof TAB_VALUES)[number];

function isTabValue(value: string | null): value is TabValue {
  return value !== null && (TAB_VALUES as readonly string[]).includes(value);
}

/**
 * Breadcrumb trail is inert while `hideHeader` is set — the visible crumb comes
 * from `getPageBreadcrumb` in the global Header — but PageShell requires the
 * prop, so it stays accurate rather than becoming a placeholder.
 */
function crumbs(label: string): { label: string; to?: string }[] {
  return [
    { label: 'Organization', to: '/dashboard' },
    { label: 'Companies', to: '/companies' },
    { label },
  ];
}

export function CompanyDetailPage(): React.ReactElement {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const { data: company, isLoading, error } = useCompany(companyId ?? null);
  const { data: rawSetupStatus, isLoading: setupLoading } = useSetupWizardStatus(companyId);
  const setupStatus = isCompanySetupStatus(rawSetupStatus) ? rawSetupStatus : null;
  const deleteCompany = useDeleteCompany();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Tab lives in the URL so a refresh or a shared link lands where it left off.
  const tabParam = searchParams.get('tab');
  const requestedTab: TabValue = isTabValue(tabParam) ? tabParam : 'overview';
  const activeTab: TabValue = requestedTab;
  const canOpenHr =
    !permissionsLoading &&
    (['employment', 'structure', 'movements', 'probation'] as const).some((action) =>
      can('hr', action)
    );

  const handleTabChange = (value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    setSearchParams(next, { replace: true });
  };

  // Old `#members` deep links resolve to the workspace Members page filtered by company.
  useEffect(() => {
    if (hash === '#members' && companyId) {
      navigate(`/members?companyId=${companyId}`, { replace: true });
    }
  }, [hash, companyId, navigate]);

  const handleConfirmDelete = (): void => {
    if (companyId) {
      deleteCompany.mutate(companyId, {
        onSuccess: () => navigate('/companies', { replace: true }),
      });
      setDeleteConfirmOpen(false);
    }
  };

  if (!companyId) {
    return <Navigate to="/companies" replace />;
  }
  const hrRedirect = tabParam ? HR_REDIRECTS[tabParam] : undefined;
  if (hrRedirect) {
    const employee = searchParams.get('employee');
    const employeeParams = employee ? `&employee=${employee}&employeeCompany=${companyId}` : '';
    return <Navigate to={`/hr?${hrRedirect}&companyId=${companyId}${employeeParams}`} replace />;
  }

  if (isLoading) {
    return (
      <PageShell breadcrumbItems={crumbs('…')} title="…" className="gap-0" hideHeader>
        <SectionLoader variant="detail" label="Loading company" />
      </PageShell>
    );
  }

  if (error || !company) {
    return (
      <PageShell
        breadcrumbItems={crumbs('Company')}
        title="Company not found"
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <p className="text-sm text-danger">{error ? error.message : 'Company not found.'}</p>
          <Button variant="secondary" onClick={() => navigate('/companies')}>
            Back to companies
          </Button>
        </div>
      </PageShell>
    );
  }

  const description = [company.code ? `Code: ${company.code}` : null, `Status: ${company.status}`]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <PageShell
      breadcrumbItems={crumbs(company.name)}
      icon={Building2}
      title={company.name}
      description={description}
      className="gap-0"
      hideHeader
    >
      <WorkspaceToolbar
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canOpenHr && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/hr?tab=people&companyId=${companyId}`)}
              >
                <UsersRound className="mr-2 h-4 w-4" aria-hidden />
                People & HR
              </Button>
            )}
            <PermissionGate module="import_setup" action="read">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/companies/${companyId}/setup-wizard#import`)}
              >
                <Upload className="mr-2 h-4 w-4" aria-hidden />
                Add members
              </Button>
            </PermissionGate>
            <PermissionGate module="companies" action="delete">
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
                loading={deleteCompany.isPending}
              >
                Delete company
              </Button>
            </PermissionGate>
          </div>
        }
      />

      {/* One measure for everything below the flush toolbar: the setup strip,
          the tabs and the tab content all share this left and right edge. */}
      <div className="mx-auto w-full max-w-[1500px]">
        <PermissionGate module="import_setup" action="read">
          <SetupChecklistStrip
            companyId={companyId}
            status={setupStatus}
            isLoading={setupLoading}
          />
        </PermissionGate>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-5">
          <TabsList className="inline-flex w-auto gap-1 overflow-x-auto rounded-[var(--radius-panel)] border border-gray-200 bg-white p-1 shadow-[var(--shadow-panel)] dark:border-gray-700/80 dark:bg-gray-800 dark:shadow-none">
            <TabsTrigger
              value="overview"
              className="rounded-md px-3 py-1.5 text-sm transition-colors duration-150 hover:bg-gray-50 aria-selected:bg-primary/10 aria-selected:text-primary dark:hover:bg-gray-700/50 dark:aria-selected:text-sky-400"
            >
              <span className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" aria-hidden />
                Overview
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="rounded-md px-3 py-1.5 text-sm transition-colors duration-150 hover:bg-gray-50 aria-selected:bg-primary/10 aria-selected:text-primary dark:hover:bg-gray-700/50 dark:aria-selected:text-sky-400"
            >
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden />
                Company Info
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <CompanyOverviewTab company={company} />
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <CompanySettingsTab company={company} />
          </TabsContent>
        </Tabs>
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete company"
        message={`Delete "${company.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteCompany.isPending}
      />
    </PageShell>
  );
}
